import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, VStack, HStack, Heading, Text, Button, Table, Thead, Tbody, Tr, Th, Td,
  useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalCloseButton, ModalFooter, FormControl, FormLabel, Input, useToast,
  Badge, Spinner, IconButton, Tooltip, Select, Switch, Collapse, Icon,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon, DownloadIcon, RepeatIcon, ChevronDownIcon, ChevronRightIcon, CopyIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import api, { API_BASE_URL } from '../services/api';
import { useLanguage } from '../hooks/useLanguage';

interface UserData {
  id: number;
  username: string;
  email: string;
  full_name: string;
  unit_id: string | null;
  is_active: boolean;
  created_at: string;
  roles: { id: number; name: string }[];
}

interface Department {
  unit_id: string;
  department_name: string;
  work_unit: string | null;
}

const TABLE_COLGROUP = (
  <colgroup>
    <col style={{ width: '14%' }} />
    <col style={{ width: '22%' }} />
    <col style={{ width: '24%' }} />
    <col style={{ width: '10%' }} />
    <col style={{ width: '10%' }} />
    <col style={{ width: '20%' }} />
  </colgroup>
);

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [availableRoles, setAvailableRoles] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editItem, setEditItem] = useState<UserData | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [expandedDepts, setExpandedDepts] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const toast = useToast();
  const { t } = useLanguage();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    unit_id: '',
    password: '',
    roleIds: [] as number[],
  });

  const fetchData = async () => {
    try {
      const [usersRes, deptsRes, rolesRes] = await Promise.allSettled([
        api.get('/api/users/').then(r => r.data),
        api.get('/api/departments/').then(r => r.data),
        api.get('/api/roles/').then(r => r.data),
      ]);
      if (usersRes.status === 'fulfilled') setUsers(usersRes.value);
      if (deptsRes.status === 'fulfilled') {
        setDepartments(deptsRes.value);
        const allDeptIds = new Set<string>(deptsRes.value.map((d: Department) => d.unit_id));
        setExpandedDepts(allDeptIds);
      }
      if (rolesRes.status === 'fulfilled') setAvailableRoles(rolesRes.value);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const getDeptName = (unitId: string | null) => {
    if (!unitId) return t('users.noDept');
    const dept = departments.find(d => d.unit_id === unitId);
    return dept
      ? (dept.work_unit ? `${dept.department_name} (${dept.work_unit})` : dept.department_name)
      : unitId;
  };

  const { adminUsers, groupedUsers, deptOrder } = useMemo(() => {
    const filtered = users.filter(u =>
      u.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    const admins = filtered.filter(u => u.roles.some(r => r.name === 'admin'));
    const nonAdmins = filtered.filter(u => !u.roles.some(r => r.name === 'admin'));

    const groups: Record<string, UserData[]> = {};
    for (const u of nonAdmins) {
      const key = u.unit_id || '__none__';
      if (!groups[key]) groups[key] = [];
      groups[key].push(u);
    }
    const order = Object.keys(groups).sort((a, b) => {
      if (a === '__none__') return 1;
      if (b === '__none__') return -1;
      return a.localeCompare(b);
    });
    return { adminUsers: admins, groupedUsers: groups, deptOrder: order };
  }, [users, searchTerm]);

  const toggleDept = (deptId: string) => {
    setExpandedDepts(prev => {
      const next = new Set(prev);
      if (next.has(deptId)) next.delete(deptId);
      else next.add(deptId);
      return next;
    });
  };

  const handleCreate = (defaultUnitId?: string) => {
    setEditItem(null);
    setFormData({ username: '', email: '', full_name: '', unit_id: defaultUnitId || '', password: '', roleIds: [] });
    setGeneratedPassword('');
    setShowPassword(false);
    onOpen();
  };

  const handleEdit = (item: UserData) => {
    setEditItem(item);
    setFormData({
      username: item.username,
      email: item.email,
      full_name: item.full_name,
      unit_id: item.unit_id || '',
      password: '',
      roleIds: item.roles.map(r => r.id),
    });
    setGeneratedPassword('');
    setShowPassword(false);
    onOpen();
  };

  const genPassword = async () => {
    try {
      const res = await api.get('/api/users/generate-password');
      const data = res.data;
      setGeneratedPassword(data.password);
      setFormData(prev => ({ ...prev, password: data.password }));
      setShowPassword(true);
    } catch (error) {
      toast({ title: t('users.genPasswordError'), status: 'error' });
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(generatedPassword);
    toast({ title: t('users.passwordCopied'), status: 'success', duration: 2000 });
  };

  const handleSave = async () => {
    try {
      if (editItem) {
        const body: any = {
          email: formData.email,
          full_name: formData.full_name,
          unit_id: formData.unit_id || null,
          role_ids: formData.roleIds,
        };
        if (formData.password) body.password = formData.password;
        await api.put(`/api/users/${editItem.id}`, body);
        toast({ title: t('users.updatedSuccess'), status: 'success' });
      } else {
        if (!formData.username || !formData.email || !formData.full_name || !formData.password) {
          toast({ title: t('users.fillRequired'), status: 'warning' });
          return;
        }
        await api.post('/api/users/', {
          username: formData.username,
          email: formData.email,
          full_name: formData.full_name,
          unit_id: formData.unit_id || null,
          password: formData.password,
          role_ids: formData.roleIds,
        });
        toast({ title: t('users.createdSuccess'), status: 'success' });
      }
      fetchData();
      onClose();
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || error.message || t('users.saveError'), status: 'error' });
    }
  };

  const handleDelete = async (userId: number) => {
    if (!window.confirm(t('users.confirmDelete'))) return;
    try {
      await api.delete(`/api/users/${userId}`);
      toast({ title: t('users.deletedSuccess'), status: 'success' });
      fetchData();
    } catch (error) {
      toast({ title: t('users.deleteError'), status: 'error' });
    }
  };

  const handleExportHTML = async () => {
    try {
      const res = await api.get('/api/users/export/html', { responseType: 'blob' });
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({ title: t('users.exportError'), status: 'error' });
    }
  };

  const renderUserRow = (user: UserData) => (
    <Tr key={user.id} _hover={{ bg: 'gray.50' }}>
      <Td fontWeight="bold" fontSize="sm">{user.username}</Td>
      <Td>{user.full_name}</Td>
      <Td fontSize="sm">{user.email}</Td>
      <Td>
        <HStack spacing={1} flexWrap="wrap">
          {user.roles.map(r => (
            <Badge key={r.id} colorScheme={r.name === 'admin' ? 'purple' : 'blue'} borderRadius="full" fontSize="xs">{r.name}</Badge>
          ))}
        </HStack>
      </Td>
      <Td>
        <Badge colorScheme={user.is_active ? 'green' : 'red'} borderRadius="full">
          {user.is_active ? t('users.active') : t('users.inactive')}
        </Badge>
      </Td>
      <Td>
        <HStack spacing={1}>
          <Tooltip label={t('common.edit')}><IconButton aria-label={t('common.edit')} icon={<EditIcon />} size="xs" colorScheme="yellow" variant="ghost" onClick={() => handleEdit(user)} /></Tooltip>
          <Tooltip label={t('common.delete')}><IconButton aria-label={t('common.delete')} icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost" onClick={() => handleDelete(user.id)} /></Tooltip>
        </HStack>
      </Td>
    </Tr>
  );

  const renderTable = (tableUsers: UserData[]) => (
    <Table variant="simple" size="sm" style={{ tableLayout: 'fixed' }}>
      {TABLE_COLGROUP}
      <Thead bg="#0F2C59">
        <Tr>
          <Th color="white">{t('users.username')}</Th>
          <Th color="white">{t('users.fullName')}</Th>
          <Th color="white">{t('users.email')}</Th>
          <Th color="white">{t('users.role')}</Th>
          <Th color="white">{t('users.status')}</Th>
          <Th color="white">{t('common.actions')}</Th>
        </Tr>
      </Thead>
      <Tbody>
        {tableUsers.map(renderUserRow)}
      </Tbody>
    </Table>
  );

  if (loading) return <Spinner size="xl" display="block" mx="auto" mt={10} />;

  return (
    <Box>
      <VStack spacing={4} align="stretch">
        <HStack justifyContent="space-between" flexWrap="wrap">
          <Heading size="lg" color="blue.600">{t('users.title', { n: users.length })}</Heading>
          <HStack spacing={2}>
            <Input size="sm" placeholder={t('common.search')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} maxW="200px" />
            <Button leftIcon={<DownloadIcon />} colorScheme="teal" variant="outline" size="sm" onClick={handleExportHTML}>{t('users.export')}</Button>
            <Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={() => handleCreate()}>{t('users.addUser')}</Button>
          </HStack>
        </HStack>

        {/* Admin Section */}
        {adminUsers.length > 0 && (
          <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden" borderLeft="4px solid" borderColor="purple.400">
            <HStack px={4} py={2.5} bg="purple.50" justifyContent="space-between">
              <HStack spacing={2}>
                <Heading size="sm" color="purple.700">{t('users.admin')}</Heading>
                <Badge colorScheme="purple" borderRadius="full">{adminUsers.length}</Badge>
              </HStack>
            </HStack>
            {renderTable(adminUsers)}
          </Box>
        )}

        {/* Department Sections */}
        {deptOrder.map(deptKey => {
          const deptUsers = groupedUsers[deptKey];
          const isExpanded = expandedDepts.has(deptKey);
          const deptLabel = deptKey === '__none__' ? t('users.noDept') : getDeptName(deptKey);
          return (
            <Box key={deptKey} bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
              <HStack
                px={4} py={2.5} bg="gray.50" cursor="pointer" _hover={{ bg: 'gray.100' }}
                onClick={() => toggleDept(deptKey)} justifyContent="space-between"
              >
                <HStack spacing={2}>
                  <Icon as={isExpanded ? ChevronDownIcon : ChevronRightIcon} />
                  <Heading size="sm" color="blue.700">{deptLabel}</Heading>
                  <Badge colorScheme="blue" borderRadius="full">{deptUsers.length}</Badge>
                </HStack>
                <Button size="xs" leftIcon={<AddIcon />} colorScheme="blue" variant="ghost"
                  onClick={(e) => { e.stopPropagation(); handleCreate(deptKey === '__none__' ? '' : deptKey); }}>
                  {t('common.add')}
                </Button>
              </HStack>
              <Collapse in={isExpanded} animateOpacity>
                {renderTable(deptUsers)}
              </Collapse>
            </Box>
          );
        })}
      </VStack>

      {/* Create/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editItem ? t('users.editTitle', { name: editItem.username }) : t('users.addTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              {!editItem && (
                <FormControl isRequired>
                  <FormLabel>{t('users.username')}</FormLabel>
                  <Input value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} placeholder={t('users.usernamePlaceholder')} />
                </FormControl>
              )}
              <FormControl isRequired>
                <FormLabel>{t('users.fullName')}</FormLabel>
                <Input value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} placeholder={t('users.namePlaceholder')} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('users.email')}</FormLabel>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder={t('users.emailPlaceholder')} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('users.department')}</FormLabel>
                <Select value={formData.unit_id} onChange={(e) => setFormData({ ...formData, unit_id: e.target.value })} placeholder={t('users.selectDept')}>
                  {departments.map(d => (
                    <option key={d.unit_id} value={d.unit_id}>
                      {d.unit_id} - {d.work_unit ? `${d.department_name} (${d.work_unit})` : d.department_name}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>{t('users.role')}</FormLabel>
                <Select
                  multiple
                  value={formData.roleIds.map(String)}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, opt => Number(opt.value));
                    setFormData({ ...formData, roleIds: selected });
                  }}
                  size="md"
                  minH={100}
                >
                  {availableRoles.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>{t('users.selectRoleHint')}</Text>
              </FormControl>
              <FormControl>
                <FormLabel>{editItem ? t('users.newPassword') : t('users.password')}</FormLabel>
                <HStack>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editItem ? t('users.newPasswordPlaceholder') : t('users.passwordPlaceholder')}
                  />
                  <Tooltip label={showPassword ? t('users.hide') : t('users.show')}>
                    <IconButton
                      aria-label={t('users.show')}
                      icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={() => setShowPassword(!showPassword)}
                      colorScheme="gray"
                      variant="outline"
                    />
                  </Tooltip>
                  <Tooltip label={t('users.generate')}>
                    <IconButton aria-label={t('users.generate')} icon={<RepeatIcon />} onClick={genPassword} colorScheme="teal" />
                  </Tooltip>
                </HStack>
                {generatedPassword && (
                  <HStack mt={1}>
                    <Text fontSize="xs" color="green.600">
                      <strong>{generatedPassword}</strong>
                    </Text>
                    <Button size="xs" leftIcon={<CopyIcon />} variant="link" colorScheme="blue" onClick={copyPassword}>{t('users.copy')}</Button>
                  </HStack>
                )}
                {editItem && (
                  <Text fontSize="xs" color="gray.500" mt={1}>{t('users.keepCurrentPassword')}</Text>
                )}
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>{t('common.cancel')}</Button>
            <Button colorScheme="blue" onClick={handleSave}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default UserManagement;
